// ============================================================================
// KNOWLEDGE BASE HOOKS
// React hooks for KB document management and RAG queries
// ============================================================================

import { useQuery, useMutation, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import { api } from '../services/api';

// ============================================================================
// TYPES
// ============================================================================

export interface KBDocument {
  documentId: string;
  projectId: string;
  fileName: string;
  fileType: string;
  fileSizeBytes: number;
  s3Key: string;
  uploadedBy: string;
  uploadedAt: string;
  processingStatus: 'pending' | 'processing' | 'completed' | 'failed';
  embeddingStatus: 'pending' | 'processing' | 'completed' | 'failed';
  chunkCount: number;
  vectorIds: string[];
  vectorCollectionName: string;
}

export interface UploadUrlResponse {
  uploadUrl: string;
  documentId: string;
  s3Key: string;
}

export interface SearchSource {
  documentId: string;
  fileName: string;
  content: string;
  relevanceScore: number;
  chunkIndex: number;
}

export interface SearchResponse {
  sources: SearchSource[];
  answer?: string;
}

export interface KBTestResult {
  query: string;
  description: string;
  passed: boolean;
  latencyMs: number;
  sourcesFound: number;
  avgRelevance: number;
  answer: string | null;
  topSource: { fileName: string; relevanceScore: number; preview: string } | null;
  error?: string;
}

export interface KBTestResponse {
  status: 'passed' | 'partial' | 'failed' | 'no_documents';
  message?: string;
  results: KBTestResult[];
  summary: {
    total: number;
    passed: number;
    failed: number;
    avgRelevance: number;
    avgLatencyMs: number;
    documentsIndexed: number;
  };
}

export interface KBStats {
  totalDocuments: number;
  documentsProcessed: number;
  documentsProcessing: number;
  documentsPending: number;
  documentsFailed: number;
  totalChunks: number;
  totalSizeBytes: number;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

const kbApi = {
  // List documents
  listDocuments: async (projectId: string): Promise<KBDocument[]> => {
    const response = await api.get(
      `/api/projects/${projectId}/knowledge-base`
    );
    return response.data.documents;
  },

  // Get document details
  getDocument: async (
    projectId: string,
    documentId: string
  ): Promise<KBDocument> => {
    const response = await api.get(
      `/api/projects/${projectId}/knowledge-base/${documentId}`
    );
    return response.data.document;
  },

  // Generate upload URL
  generateUploadUrl: async (
    projectId: string,
    fileName: string,
    fileType: string,
    fileSize: number
  ): Promise<UploadUrlResponse> => {
    const response = await api.post(
      `/api/projects/${projectId}/knowledge-base/upload`,
      { fileName, fileType, fileSize }
    );
    return response.data;
  },

  // Upload to S3 (uses raw axios - presigned URL, not our API)
  uploadToS3: async (
    uploadUrl: string,
    file: File,
    onProgress?: (progress: number) => void,
    contentType?: string
  ): Promise<void> => {
    await axios.put(uploadUrl, file, {
      headers: { 'Content-Type': contentType || file.type },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = (progressEvent.loaded / progressEvent.total) * 100;
          onProgress(progress);
        }
      }
    });
  },

  // Finalize upload
  finalizeUpload: async (
    projectId: string,
    documentId: string
  ): Promise<void> => {
    await api.post(
      `/api/projects/${projectId}/knowledge-base/upload/complete`,
      { documentId }
    );
  },

  // Delete document
  deleteDocument: async (
    projectId: string,
    documentId: string
  ): Promise<void> => {
    await api.delete(
      `/api/projects/${projectId}/knowledge-base/${documentId}`
    );
  },

  // Search knowledge base
  search: async (
    projectId: string,
    query: string,
    options: {
      k?: number;
      minRelevanceScore?: number;
      includeAnswer?: boolean;
    }
  ): Promise<SearchResponse> => {
    const response = await api.post(
      `/api/projects/${projectId}/knowledge-base/search`,
      { query, ...options }
    );
    return response.data;
  },

  // Test knowledge base
  testKB: async (projectId: string): Promise<KBTestResponse> => {
    const response = await api.post(
      `/api/projects/${projectId}/knowledge-base/test`,
      {},
      { timeout: 120000 } // 2 min timeout for test queries
    );
    return response.data;
  },

  // Get statistics
  getStats: async (projectId: string): Promise<KBStats> => {
    const response = await api.get(
      `/api/projects/${projectId}/knowledge-base/stats`
    );
    return response.data.stats;
  }
};

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook to list all documents in a project
 */
export const useKBDocuments = (
  projectId: string,
  token: string,
  options?: { refetchInterval?: number }
): UseQueryResult<KBDocument[], Error> => {
  return useQuery({
    queryKey: ['kb-documents', projectId],
    queryFn: () => kbApi.listDocuments(projectId),
    enabled: !!projectId && !!token,
    refetchInterval: options?.refetchInterval
  });
};

/**
 * Hook to upload a document
 */
export const useUploadDocument = (projectId: string, token: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      onProgress
    }: {
      file: File;
      onProgress?: (progress: number) => void;
    }) => {
      // Normalize MIME type — browsers (especially macOS) often report .md as empty string
      const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
      const effectiveType = file.type || (extension === 'md' ? 'text/markdown' : '');

      // Step 1: Generate upload URL
      const uploadData = await kbApi.generateUploadUrl(
        projectId,
        file.name,
        effectiveType,
        file.size
      );

      // Step 2: Upload to S3 (presigned URL, uses raw axios)
      await kbApi.uploadToS3(uploadData.uploadUrl, file, onProgress, effectiveType);

      // Step 3: Finalize upload
      await kbApi.finalizeUpload(projectId, uploadData.documentId);

      return uploadData.documentId;
    },
    onSuccess: () => {
      // Invalidate documents list to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['kb-documents', projectId] });
      queryClient.invalidateQueries({ queryKey: ['kb-stats', projectId] });
    }
  });
};

/**
 * Hook to delete a document
 */
export const useDeleteDocument = (projectId: string, token: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) =>
      kbApi.deleteDocument(projectId, documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kb-documents', projectId] });
      queryClient.invalidateQueries({ queryKey: ['kb-stats', projectId] });
    }
  });
};

/**
 * Hook to search knowledge base
 */
export const useSearchKB = (projectId: string, token: string) => {
  return useMutation({
    mutationFn: ({
      query,
      k = 5,
      minRelevanceScore = 0.7,
      includeAnswer = true
    }: {
      query: string;
      k?: number;
      minRelevanceScore?: number;
      includeAnswer?: boolean;
    }) =>
      kbApi.search(projectId, query, { k, minRelevanceScore, includeAnswer })
  });
};

/**
 * Hook to test knowledge base quality
 */
export const useTestKB = (projectId: string, token: string) => {
  return useMutation({
    mutationFn: () => kbApi.testKB(projectId)
  });
};

/**
 * Hook to get knowledge base statistics
 */
export const useKBStats = (
  projectId: string,
  token: string
): UseQueryResult<KBStats, Error> => {
  return useQuery({
    queryKey: ['kb-stats', projectId],
    queryFn: () => kbApi.getStats(projectId),
    enabled: !!projectId && !!token
  });
};

/**
 * Utility hook to format file sizes
 */
export const useFormatFileSize = () => {
  return (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };
};

/**
 * Utility hook to validate file types
 */
export const useValidateFile = () => {
  const allowedTypes = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    'text/markdown',
    'text/x-markdown',
    'text/csv',
    'image/jpeg',
    'image/png',
    'image/tiff'
  ];

  const extensionFallback: Record<string, string> = {
    md: 'text/markdown',
    pdf: 'application/pdf',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    txt: 'text/plain',
    csv: 'text/csv',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    tiff: 'image/tiff',
    tif: 'image/tiff'
  };

  const maxSizeBytes = 50 * 1024 * 1024; // 50MB

  return (file: File): { valid: boolean; error?: string } => {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    const effectiveType = file.type || extensionFallback[extension] || '';

    if (!allowedTypes.includes(effectiveType)) {
      return {
        valid: false,
        error: 'Unsupported file type. Please upload PDF, DOCX, TXT, MD, CSV, JPEG, PNG, or TIFF files.'
      };
    }

    if (file.size > maxSizeBytes) {
      return {
        valid: false,
        error: 'File size exceeds maximum of 50MB.'
      };
    }

    return { valid: true };
  };
};
